/* NEW label only: read-only display, no launch recording or catalogue updates. */
(function () {
  'use strict';
  const dayFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Dublin', year: 'numeric', month: '2-digit', day: '2-digit'
  });
  function calendarDay(date) {
    const parts = Object.fromEntries(dayFormat.formatToParts(date).map(p => [p.type, p.value]));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / 86400000;
  }
  function eligible(product, now = new Date()) {
    if (!product || product.available !== true || product.physical !== true || product.bestseller || product.excluded) return false;
    const value = product.launchedAt;
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return false;
    const launch = new Date(value);
    if (!Number.isFinite(launch.getTime()) || launch > now) return false;
    const age = calendarDay(now) - calendarDay(launch);
    return age >= 0 && age < 30;
  }
  // Used by the local boundary tests; nothing is exported in the storefront.
  if (typeof module !== 'undefined' && module.exports) module.exports = { eligible, calendarDay };
  if (typeof document === 'undefined') return;
  const source = document.getElementById('leavys-new-label-data');
  if (!source) return;
  let config;
  try { config = JSON.parse(source.textContent); } catch (_) { return; }

  // Keep fallback requests in the same draft during preview. No sample dates.
  const previewThemeId = window.Shopify && Shopify.theme && Shopify.theme.role === 'unpublished'
    ? Shopify.theme.id : null;
  const cache = new Map();
  function remember(data) {
    if (!data || !data.handle) return null;
    cache.set(data.handle, Promise.resolve(data));
    return data;
  }
  (config.products || []).forEach(remember);

  // Filter-app redraws can introduce cards outside Liquid's initial product list.
  // Resolve only nearby cards, once per handle, with at most three requests at a time.
  const queue = [];
  let running = 0;
  function drain() {
    while (running < 3 && queue.length) {
      const job = queue.shift();
      running++;
      job().finally(() => { running--; drain(); });
    }
  }
  function getProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const pending = new Promise(resolve => {
      queue.push(async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const root = (config.root || '/').replace(/\/?$/, '/');
          const url = new URL(root + 'products/' + encodeURIComponent(handle), location.origin);
          url.searchParams.set('view', 'leavys-new-label');
          if (previewThemeId) url.searchParams.set('preview_theme_id', String(previewThemeId));
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Product unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = html.querySelector('[data-leavys-new-product]');
          const data = payload && JSON.parse(payload.textContent);
          resolve(data && data.handle === handle ? remember(data) : null);
        } catch (_) { resolve(null); } finally { clearTimeout(timeout); }
      });
    });
    cache.set(handle, pending);
    drain();
    return pending;
  }

  const selector = '.relative.product_image, [data-product-gallery-wrapper]';
  const observed = new WeakSet();
  const resolved = new WeakMap();
  function handleFor(host) {
    if (host.matches('[data-product-gallery-wrapper]')) {
      const gallery = host.querySelector('[data-product-gallery][data-product-id]');
      const main = (config.products || []).find(p => p && p.handle === config.productHandle);
      return main && gallery && String(main.id) === gallery.dataset.productId ? main.handle : null;
    }
    const link = host.querySelector('a[href*="/products/"]');
    const match = link && new URL(link.href, location.origin).pathname.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
  function paint(host, product) {
    const existing = host.querySelector(':scope > .leavys-new-badge');
    // Existing Bestseller has priority. Other image-corner badges stay untouched.
    const blocked = host.querySelector('.leavys-bestseller-badge, .sale_banner, .sold_out, .price-ui-badge');
    if (!eligible(product) || blocked) {
      if (existing) existing.remove();
      host.classList.remove('leavys-new-label-host');
      return;
    }
    if (!existing) {
      host.classList.add('leavys-new-label-host');
      const badge = document.createElement('span');
      badge.className = 'leavys-new-badge';
      badge.textContent = 'NEW';
      host.appendChild(badge);
    }
  }
  async function update(host) {
    const handle = handleFor(host);
    if (!handle) return;
    const data = await getProduct(handle);
    if (!host.isConnected || handleFor(host) !== handle) return;
    resolved.set(host, { handle, data });
    paint(host, data);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { observer.unobserve(entry.target); update(entry.target); }
    });
  }, { rootMargin: '300px' }) : null;
  function scan() {
    document.querySelectorAll(selector).forEach(host => {
      const previous = resolved.get(host);
      if (previous && previous.handle === handleFor(host)) paint(host, previous.data);
      else if (previous) update(host);
      else if (!observed.has(host)) {
        observed.add(host);
        if (observer) observer.observe(host); else update(host);
      }
    });
  }
  let scheduled = false;
  new MutationObserver(records => {
    // Ignore our own badge insertion/removal to prevent mutation loops.
    if (!records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && !node.classList.contains('leavys-new-badge')))) return;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });
  scan();
  // Cached pages and tabs held open across midnight must also lose expired labels.
  setInterval(scan, 30000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scan(); });
})();
